import * as ROUTES from 'constants/routes'
import { Box, Stack, Typography } from '@mui/material'
import DeepcellPopover from 'components/shared/DeepcellPopover'
import DeepcellPrimaryButton from 'components/shared/DeepcellPrimaryButton'
import useFlagCondition from 'components/shared/useFlagCondition'
import { isExternalDemo } from 'config'
import { useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { useAuth } from 'services/auth'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { SaveSessionMenu } from './SaveSessionMenu'
import UploadRunDataTsvButton from './UploadRunDataTsvButton'

import { ShareButton } from './ShareButton'
import { UploadCellDataTsvButton } from './UploadCellDataTsvButton'
import { LabelImagesButton } from '../labeling/LabelImagesButton'

export const NavTopBarRightSideButtons = (): JSX.Element => {
  const { sessionId } = useCellVisualizationUrlParams()
  const eventsManager = useEventsManager()
  const [showContactUs, setShowContactUs] = useState(false)
  const showUploadRunDataTSV = useFlagCondition('showUploadRunDataTSV')
  const { isInternalUser } = useAuth()
  const isLabeling = useRouteMatch(ROUTES.CELL_VISUALIZATIONS_SESSION_LABEL)

  // The labeling route reuses this top bar for the session name/breadcrumb, but
  // none of the session actions (Save / Share / Label Images / uploads) apply.
  if (isLabeling) return <></>

  const buttons: JSX.Element[] = []

  if (isExternalDemo) {
    buttons.push(
      <DeepcellPrimaryButton
        key={buttons.length}
        contained
        onClick={() => {
          eventsManager.sendContactUsByEvent(new Date().getTime())
          setShowContactUs(true)
        }}
        sx={{ width: '200px', marginRight: '15px' }}
      >
        Get in touch
      </DeepcellPrimaryButton>
    )
  }

  if (sessionId && sessionId > 0) {
    if (!isExternalDemo) {
      buttons.push(<SaveSessionMenu key={buttons.length} />)
      buttons.push(<ShareButton key={buttons.length} />)
      // Internal-only entry into the cell labeling workflow
      if (isInternalUser) {
        buttons.push(<LabelImagesButton key={buttons.length} />)
      }
    }
  } else {
    // cell viz landing page
    if (isInternalUser) {
      buttons.push(<UploadCellDataTsvButton key={buttons.length} />)
    }
    if (showUploadRunDataTSV) {
      buttons.push(<UploadRunDataTsvButton key={buttons.length} />)
    }
  }

  return (
    <Stack direction="row" spacing={1}>
      {buttons}
      <DeepcellPopover
        open={showContactUs}
        sx={{
          '& .MuiPopover-paper': {
            width: '450px',
            minHeight: '300px',
            inset: 'unset !important',
            top: '48px !important',
            right: '16px !important',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
        onClose={() => setShowContactUs(false)}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              mb: 3,
              fontSize: '1.5rem',
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: 'text.secondary',
              lineHeight: 1.6,
              fontSize: '1rem',
            }}
          >
            If you have any inquiries or questions, please reach out to us via email:
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.main',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              component="a"
              href="mailto:express@deepcellbio.com"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '1.2rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              express@deepcellbio.com
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            We will get back to you within 24 hours
          </Typography>
        </Box>
      </DeepcellPopover>
    </Stack>
  )
}

export default NavTopBarRightSideButtons
